/**
 * Choose the API base URL from the generated config and the per-developer
 * overrides.
 *
 * Its own module, with no React Native or Expo imports, so it can be tested
 * directly in Node rather than asserted against as source text — the same
 * reason `parse-bool-env.ts` is separate.
 *
 * ── Why the overrides are ignored in production ──────────────────────────────
 *
 * `EXPO_PUBLIC_*` variables are INLINED INTO THE BUNDLE by `expo export` at
 * build time. They are not read at runtime, so whatever is in a developer's
 * `.env.local` when a release is built is baked into the shipped binary.
 *
 * This is not hypothetical. Measured on this repo: a production-configured
 * release `.aab` carried `http://192.168.87.37:3000` from an untracked
 * `.env.local`, while `https://api.opncue.com` sat in the bundle unused —
 * because the override was consulted BEFORE the generated config. The build
 * succeeded and reported nothing.
 *
 * Local `.env` files are git-ignored, so a CI build from a fresh checkout does
 * not have one. That makes CI safe by accident rather than by design, and "we
 * always build in CI" is a convention, not a guard.
 *
 * So in a production build the overrides do not apply at all. The override
 * exists to point a device at a LAN host during development, which is exactly
 * what its own doc comment in `config.ts` has always said it was for; there is
 * no development need it serves in a production artifact, and no legitimate
 * caller — no workflow, script or `eas.json` profile in this repository sets
 * `EXPO_PUBLIC_API_URL`.
 *
 * `scripts/assert-production-artifact.mjs` independently refuses any artifact
 * containing an unexpected local host. Both exist on purpose: this stops the
 * wrong URL being SELECTED, the guard stops a wrong artifact being SHIPPED, and
 * neither depends on the other being correct.
 */

export type ApiBaseUrlSources = {
  /** `environment` from the generated public config. */
  environment: string;
  /** `EXPO_PUBLIC_API_URL`, inlined at build time. */
  envOverride?: string;
  /** `Constants.expoConfig.extra.apiUrl`, baked into the app manifest. */
  extraOverride?: string;
  /** `apiBaseUrl` from the generated public config. */
  configuredUrl?: string;
};

export function selectApiBaseUrl({
  environment,
  envOverride,
  extraOverride,
  configuredUrl,
}: ApiBaseUrlSources): string | undefined {
  // Compared against the one value that means production. Anything else —
  // including an absent or misspelled environment — keeps the development
  // behaviour, so a config that fails to say "prod" never silently gains
  // production's stricter semantics while still being treated as production
  // elsewhere.
  if (environment === "prod") return configuredUrl;
  return envOverride ?? extraOverride ?? configuredUrl;
}
